import { oneOf, setParam } from '../shared/useUrlState';

/**
 * The address of an OpenBao view.
 *
 * Modelled on upstream's router (openbao `ui/app/router.js`), which makes the
 * namespace, the mount, the browse path, the open secret and its version all
 * addressable. Kept as query parameters rather than upstream's path segments
 * so the page stays one Backstage route and reads the same way as the
 * Kubernetes console.
 *
 * Deliberately absent: which secret values the viewer has unmasked. Paths and
 * version numbers name a thing; a link that reveals its contents on open
 * turns a pasted URL into a disclosure.
 */
export interface OpenBaoUrlState {
  /** OpenBao namespace; empty is root. */
  ns: string;
  tab: OpenBaoTab;
  /** KV mount path, without a trailing slash. Empty means "first available". */
  mount: string;
  /** Browse prefix inside the mount. Empty is the mount root — a real path. */
  path: string;
  /** Full path of the open secret; empty when none is open. */
  secret: string;
  /** KV v2 version; 0 means the current one. */
  version: number;
  /** Selected policy name on the policies tab. */
  policy: string;
}

export const OPENBAO_TABS = [
  'secrets',
  'policies',
  'access',
  'token',
] as const;
export type OpenBaoTab = (typeof OPENBAO_TABS)[number];

export const DEFAULT_OPENBAO_TAB: OpenBaoTab = 'secrets';

export const EMPTY_OPENBAO_URL_STATE: OpenBaoUrlState = {
  ns: '',
  tab: DEFAULT_OPENBAO_TAB,
  mount: '',
  path: '',
  secret: '',
  version: 0,
  policy: '',
};

/** Moving between these is navigation; anything else refines the view. */
export const OPENBAO_NAVIGATION_KEYS = [
  'ns',
  'tab',
  'mount',
  'path',
  'secret',
  'policy',
] as const satisfies readonly (keyof OpenBaoUrlState)[];

export function parseOpenBaoUrl(params: URLSearchParams): OpenBaoUrlState {
  const version = Number(params.get('version'));
  return {
    ns: params.get('ns') ?? '',
    tab: oneOf(params.get('tab'), OPENBAO_TABS, DEFAULT_OPENBAO_TAB),
    mount: (params.get('mount') ?? '').replace(/\/$/, ''),
    path: params.get('path') ?? '',
    secret: params.get('secret') ?? '',
    version: Number.isInteger(version) && version > 0 ? version : 0,
    policy: params.get('policy') ?? '',
  };
}

export function serializeOpenBaoUrl(state: OpenBaoUrlState): URLSearchParams {
  const params = new URLSearchParams();
  setParam(params, 'ns', state.ns);
  setParam(params, 'tab', state.tab, DEFAULT_OPENBAO_TAB);

  if (state.tab === 'secrets') {
    setParam(params, 'mount', state.mount);
    // Only meaningful with a mount to be relative to.
    if (state.mount) {
      setParam(params, 'path', state.path);
      setParam(params, 'secret', state.secret);
      // A version addresses a revision of an open secret, nothing on its own.
      if (state.secret && state.version > 0) {
        params.set('version', String(state.version));
      }
    }
  }
  if (state.tab === 'policies') setParam(params, 'policy', state.policy);
  return params;
}
